<template>
  <div>
    <div class="flex flex-col gap-3" data-testid="buildingDevicesGrid">
      <div
        v-for="(floorData, floorName) in buildingStruct"
        :key="floorName"
        class="flex items-center gap-2"
      >
        <div
          class="w-10 shrink-0 text-right text-xs font-bold text-dark-4 dark:text-light-4"
        >
          {{ formatFloorName(String(floorName)) }}
        </div>

        <div class="flex flex-wrap gap-2">
          <div
            v-if="floorData.entrance"
            class="relative flex flex-col items-center justify-center overflow-hidden rounded-md border border-dashed border-light-4 dark:border-dark-3 bg-light-1 dark:bg-dark-1 transition-colors"
            :class="[tileStatusClass(floorData.entrance.id)]"
            :style="{ width: tileWidth + 'px', height: tileHeight + 'px' }"
            data-testid="roomGroupOverviewTile"
          >
            <em
              v-if="statusPerRoomGroup.get(floorData.entrance.id)"
              class="mdi absolute top-1 left-1"
              :class="statusIconClass(floorData.entrance.id)"
              :style="{ fontSize: badgeFontSize + 'px' }"
            />
            <span
              class="truncate px-2 font-semibold leading-none text-dark-3 dark:text-light-3"
              :style="{ fontSize: labelFontSize + 'px' }"
            >
              {{ floorData.entrance.number ?? floorData.entrance.ordinal }}
            </span>
            <span
              v-if="
                floorData.entrance.getLabel() &&
                floorData.entrance.getLabel() !== allgemeineNutzung
              "
              class="truncate w-full px-2 text-center leading-none text-gray-400 dark:text-gray-500"
              :style="{ fontSize: nameFontSize + 'px' }"
            >
              {{ floorData.entrance.getLabel() }}
            </span>
            <div
              v-if="dmgw6RoomGroupId === floorData.entrance.id"
              class="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-0.5 text-white"
              :class="[
                dmgwBarStyle.bg,
                dmgwBarStyle.animate ? 'animate-pulse' : '',
              ]"
              :style="{ fontSize: '9px', padding: '1px 0' }"
            >
              <em class="mdi" :class="dmgwBarStyle.icon" />
              <span class="font-bold">{{ dmgwBarStyle.label }}</span>
            </div>
            <div
              v-if="errorCountPerRoomGroup.get(floorData.entrance.id)"
              class="absolute right-1 top-1 rounded-sm bg-red-500 font-bold text-white"
              :style="{ fontSize: badgeFontSize + 'px', padding: '1px 3px' }"
            >
              {{ errorCountPerRoomGroup.get(floorData.entrance.id) }}x
            </div>
            <em
              v-if="!floorData.entrance.isAccessible"
              class="mdi mdi-lock absolute bottom-1 right-1 text-light-warning dark:text-dark-warning"
              :style="{ fontSize: badgeFontSize + 'px' }"
            />
          </div>

          <div
            v-if="floorData.entrance && floorData.roomgroups.length"
            class="w-1"
          />

          <div
            v-for="rg in floorData.roomgroups"
            :key="rg.id"
            class="relative flex flex-col items-center justify-center overflow-hidden rounded-md border border-light-3 dark:border-dark-3 bg-light-2 dark:bg-dark-2 transition-colors"
            :class="[tileStatusClass(rg.id)]"
            :style="{ width: tileWidth + 'px', height: tileHeight + 'px' }"
            data-testid="roomGroupOverviewTile"
          >
            <em
              v-if="statusPerRoomGroup.get(rg.id)"
              class="mdi absolute top-1 left-1"
              :class="statusIconClass(rg.id)"
              :style="{ fontSize: badgeFontSize + 'px' }"
            />
            <span
              class="truncate px-2 font-semibold leading-none text-dark-3 dark:text-light-3"
              :style="{ fontSize: labelFontSize + 'px' }"
            >
              {{ rg.number ?? rg.ordinal }}
            </span>
            <span
              v-if="rg.getLabel() && rg.getLabel() !== allgemeineNutzung"
              class="truncate w-full px-2 text-center leading-none text-gray-400 dark:text-gray-500"
              :style="{ fontSize: nameFontSize + 'px' }"
            >
              {{ rg.getLabel() }}
            </span>
            <div
              v-if="dmgw6RoomGroupId === rg.id"
              class="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-0.5 text-white"
              :class="[
                dmgwBarStyle.bg,
                dmgwBarStyle.animate ? 'animate-pulse' : '',
              ]"
              :style="{ fontSize: '9px', padding: '1px 0' }"
            >
              <em class="mdi" :class="dmgwBarStyle.icon" />
              <span class="font-bold">{{ dmgwBarStyle.label }}</span>
            </div>
            <div
              v-if="errorCountPerRoomGroup.get(rg.id)"
              class="absolute right-1 top-1 rounded-sm bg-red-500 font-bold text-white"
              :style="{ fontSize: badgeFontSize + 'px', padding: '1px 3px' }"
            >
              {{ errorCountPerRoomGroup.get(rg.id) }}x
            </div>
            <em
              v-if="!rg.isAccessible"
              class="mdi mdi-lock absolute bottom-1 right-1 text-light-warning dark:text-dark-warning"
              :style="{ fontSize: badgeFontSize + 'px' }"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { BuildingStructure } from '@/composables/useBuildingOverview';
import { useRoomStore } from '@/store/entities/roomStore';
import { useDeviceStore } from '@/store/entities/deviceStore';
import {
  Dmgw6DeviceFunkcheckStatus,
  useDmgw6Store,
} from '@/store/dmgw6/dmgw6Store';
import { useQToolOperations } from '@/composables/useQToolOperations';
import { allgemeineNutzung } from '@/enums/roomgroup';
import { deviceTypes } from '@/enums/device';

const props = defineProps<{ buildingStruct: BuildingStructure }>();

const tileWidth = 120;
const tileHeight = 56;
const badgeFontSize = 13;
const labelFontSize = 14;
const nameFontSize = 11;

const { gatewayReadout } = useQToolOperations();

const dmgwBarStyle = computed(() => {
  const status = gatewayReadout.value.status;
  switch (status) {
    case 'reading':
      return {
        bg: 'bg-blue-500',
        icon: 'mdi-access-point',
        label: 'Auslesen',
        animate: true,
      };
    case 'fetching-key':
    case 'connecting':
      return {
        bg: 'bg-amber-500',
        icon: 'mdi-access-point',
        label: 'Verbinden',
        animate: true,
      };
    case 'done':
      return {
        bg: 'bg-green-500',
        icon: 'mdi-check-circle',
        label: 'DMGW',
        animate: false,
      };
    case 'error':
      return {
        bg: 'bg-red-500',
        icon: 'mdi-alert-circle',
        label: 'Fehler',
        animate: false,
      };
    default:
      return {
        bg: 'bg-gray-400 dark:bg-gray-600',
        icon: 'mdi-access-point',
        label: 'DMGW',
        animate: false,
      };
  }
});

function formatFloorName(floorName: string): string {
  if (floorName === 'EG') return 'EG';
  const parts = floorName.split('-');
  return `${parts[1]} ${parts[0]}`;
}

function allRoomGroups() {
  return Object.values(props.buildingStruct).flatMap((floor) => [
    ...(floor.entrance ? [floor.entrance] : []),
    ...floor.roomgroups,
  ]);
}

const dmgw6RoomGroupId = computed<string | null>(() => {
  const readoutState = gatewayReadout.value;
  const deviceStore = useDeviceStore();

  let gateway;
  if ('gateway' in readoutState && readoutState.gateway) {
    const serial = readoutState.gateway.serialNumber;
    gateway = [...deviceStore.devices.values()].find(
      (d) =>
        d.deviceType === deviceTypes.DirectMeterGateway &&
        d.serialNumber === serial
    );
  }

  if (!gateway) {
    gateway = deviceStore.getDirectMeterGateway();
  }

  if (!gateway) return null;
  for (const rg of allRoomGroups()) {
    const rooms = useRoomStore().getRoomsByRoomGroupId(rg.id);
    for (const room of rooms) {
      const devices = deviceStore.getAllDevicesInRoom(room.id);
      if (devices.some((d) => d.id === gateway!.id)) return rg.id;
    }
  }
  return null;
});

const statusPerRoomGroup = computed<Map<string, Dmgw6DeviceFunkcheckStatus>>(
  () => {
    const result = new Map<string, Dmgw6DeviceFunkcheckStatus>();
    const dmgw6Store = useDmgw6Store();
    if (!dmgw6Store.hasFunkcheckStatuses) return result;

    for (const rg of allRoomGroups()) {
      const rooms = useRoomStore().getRoomsByRoomGroupId(rg.id);
      const roomStatuses = rooms
        .map((room) => dmgw6Store.getRoomFunkcheckStatus(room.id))
        .filter((s): s is Dmgw6DeviceFunkcheckStatus => s !== undefined);

      if (roomStatuses.length === 0) continue;

      if (roomStatuses.some((s) => s === Dmgw6DeviceFunkcheckStatus.error)) {
        result.set(rg.id, Dmgw6DeviceFunkcheckStatus.error);
      } else if (
        roomStatuses.some((s) => s === Dmgw6DeviceFunkcheckStatus.pending)
      ) {
        result.set(rg.id, Dmgw6DeviceFunkcheckStatus.pending);
      } else if (
        roomStatuses.some((s) => s === Dmgw6DeviceFunkcheckStatus.partial)
      ) {
        result.set(rg.id, Dmgw6DeviceFunkcheckStatus.partial);
      } else if (
        roomStatuses.every((s) => s === Dmgw6DeviceFunkcheckStatus.disconnected)
      ) {
        result.set(rg.id, Dmgw6DeviceFunkcheckStatus.disconnected);
      } else {
        result.set(rg.id, Dmgw6DeviceFunkcheckStatus.success);
      }
    }
    return result;
  }
);

const errorCountPerRoomGroup = computed<Map<string, number>>(() => {
  const result = new Map<string, number>();
  const statuses = useDmgw6Store().deviceFunkcheckStatuses;
  if (statuses.size === 0) return result;
  for (const rg of allRoomGroups()) {
    let count = 0;
    const rooms = useRoomStore().getRoomsByRoomGroupId(rg.id);
    for (const room of rooms) {
      for (const device of useDeviceStore().getAllDevicesInRoom(room.id)) {
        if (statuses.get(device.id) === Dmgw6DeviceFunkcheckStatus.error)
          count++;
      }
    }
    if (count > 0) result.set(rg.id, count);
  }
  return result;
});

function tileStatusClass(rgId: string): string {
  const status = statusPerRoomGroup.value.get(rgId);
  switch (status) {
    case Dmgw6DeviceFunkcheckStatus.success:
      return 'bg-green-50 dark:bg-green-950/20 border-green-300 dark:border-green-800';
    case Dmgw6DeviceFunkcheckStatus.pending:
      return 'bg-gray-100 dark:bg-gray-800/30 border-gray-300 dark:border-gray-600';
    case Dmgw6DeviceFunkcheckStatus.partial:
      return 'bg-orange-50 dark:bg-orange-950/20 border-orange-300 dark:border-orange-800';
    case Dmgw6DeviceFunkcheckStatus.error:
      return 'bg-red-50 dark:bg-red-950/20 border-red-300 dark:border-red-800';
    case Dmgw6DeviceFunkcheckStatus.disconnected:
      return 'bg-gray-50 dark:bg-gray-800/20 border-gray-300 dark:border-gray-600';
    default:
      return '';
  }
}

function statusIconClass(rgId: string): string {
  const status = statusPerRoomGroup.value.get(rgId);
  switch (status) {
    case Dmgw6DeviceFunkcheckStatus.success:
      return 'mdi-check-circle text-green-500';
    case Dmgw6DeviceFunkcheckStatus.pending:
      return 'mdi-loading mdi-spin text-gray-400';
    case Dmgw6DeviceFunkcheckStatus.partial:
      return 'mdi-check-circle text-yellow-400';
    case Dmgw6DeviceFunkcheckStatus.error:
      return 'mdi-alert-circle text-red-500';
    case Dmgw6DeviceFunkcheckStatus.disconnected:
      return 'mdi-bluetooth-off text-gray-400';
    default:
      return '';
  }
}
</script>
